import React, { useCallback, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { Modal, Props as ModalProps, RenderType } from './modal';
import { Position, ToastContextProvider } from '@/contexts/toast-context';
import { SCREEN_PADDING } from '@/styles';
import { StatusBar, StyleProp, ViewStyle } from 'react-native';
import { TooltipContextProvider } from '@/contexts/tooltip-context';
import { useIsMounted } from '@/hooks/use-is-mounted';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type BaseModalProps = Exclude<Partial<ModalProps>, 'children'>;

export type Props = BaseModalProps & {
  contentStyle?: StyleProp<ViewStyle>;
  onClose: () => void;
  onModalHide?: () => void;
  onOpen?: () => void;
  showCloseButton?: boolean;
  showToastsExternally?: boolean;
  toastContainerStyle?: StyleProp<ViewStyle>;
  toastPosition?: Position;
};

export const DEFAULT_PADDING = 24;

export const BaseActionSheet: React.FC<Props> = ({
  children,
  contentStyle,
  onClose,
  onModalHide,
  onOpen,
  showCloseButton = true,
  showToastsExternally,
  toastContainerStyle,
  toastPosition,
  style,
  ...rest
}) => {
  const isMounted = useIsMounted();
  const { bottom } = useSafeAreaInsets();
  const { isSmallLayout } = useLayoutContext();
  const [tooltipsEnabled, setTooltipsEnabled] = useState(false);
  const [styles] = useStyles();

  const getStyle = useCallback(
    (renderType: RenderType): StyleProp<ViewStyle> => {
      return renderType === RenderType.POPOVER
        ? [
            styles.popoverBase,
            typeof style === 'function' ? style(renderType) : style,
          ]
        : isSmallLayout
          ? [
              styles.base,
              typeof style === 'function' ? style(renderType) : style,
            ]
          : null;
    },
    [isSmallLayout, styles, style],
  );

  const getContentContainerStyle = useCallback(
    (renderType: RenderType): StyleProp<ViewStyle> => {
      return [
        styles.content,
        renderType === RenderType.POPOVER
          ? styles.popoverContent
          : isSmallLayout
            ? { paddingBottom: Math.max(bottom, SCREEN_PADDING) }
            : styles.modalContent,
        contentStyle,
      ];
    },
    [bottom, contentStyle, isSmallLayout, styles],
  );

  return (
    <Modal
      animationOutTiming={200}
      avoidKeyboard
      contentContainerStyle={getContentContainerStyle}
      // Fixes a bug where the modal flashes as it animates
      hideModalContentWhileAnimating
      onBackButtonPress={onClose}
      onBackdropPress={onClose}
      onModalCloseButtonPress={showCloseButton ? onClose : undefined}
      onModalHide={() => {
        if (isMounted()) {
          setTooltipsEnabled(false);
        }
        onModalHide?.();
      }}
      onModalShow={() => {
        if (isMounted()) {
          onOpen?.();
          setTooltipsEnabled(true);
        }
      }}
      style={getStyle}
      useNativeDriver={false}
      {...rest}
    >
      <StatusBar
        // backgroundColor is only applicable to Android - set so it blends in
        // with the action sheet overlay
        backgroundColor="rgba(0, 0, 0, 0.5)"
        barStyle="dark-content"
      />
      {showToastsExternally ? (
        <TooltipContextProvider enabled={tooltipsEnabled}>
          {children}
        </TooltipContextProvider>
      ) : (
        <ToastContextProvider
          containerStyle={toastContainerStyle}
          position={toastPosition}
        >
          <TooltipContextProvider enabled={tooltipsEnabled}>
            {children}
          </TooltipContextProvider>
        </ToastContextProvider>
      )}
    </Modal>
  );
};

export const ACTION_SHEET_BORDER_RADIUS = 16;

const useStyles = createStyles((theme) => ({
  base: {
    justifyContent: 'flex-end',
    margin: 0,
  },
  content: {
    backgroundColor: theme.colors.white,
    borderTopLeftRadius: ACTION_SHEET_BORDER_RADIUS,
    borderTopRightRadius: ACTION_SHEET_BORDER_RADIUS,
    paddingHorizontal: DEFAULT_PADDING,
    paddingTop: 32,
  },
  modalContent: {
    paddingBottom: DEFAULT_PADDING,
    paddingTop: 0,
  },
  popoverBase: {
    margin: 0,
  },
  popoverContent: {
    paddingTop: 0,
    width: 'auto',
  },
}));
